import { Button, Flex, Heading, IconButton, Popover, Select, Separator, Text, Tooltip } from '@radix-ui/themes';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { TbBook, TbCopy, TbLogout, TbRobot, TbUser, TbUsersGroup } from 'react-icons/tb';
import { useLocalStorage } from 'usehooks-ts';
import { Avatar } from '~/components/Avatar';
import { toast } from '~/components/Toast';
import { useSession } from '~/features/auth/context/Session';
import { useAlphabetisedOrganisations } from '~/hooks/useAlphabetisedOrganisations';
import { useOrganisation } from '~/hooks/useOrganisation';
import Styles from './css/account-dropdown.module.scss';

export function AccountDropdown({
  isPopoverOpen,
  setIsPopoverOpen,
  handleNavigationToAccount,
}: {
  isPopoverOpen: boolean;
  setIsPopoverOpen: (isPopoverOpen: boolean) => void;
  handleNavigationToAccount?: (route: string, search?: Record<string, unknown>) => void;
}) {
  const { organisation, organisations, onUpdateActiveOrganisation } = useOrganisation();
  const { session, handleLogout } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [showAutomations] = useLocalStorage(
    'enableAutomations',
    localStorage.getItem('enableAutomations') === 'true' || false
  );

  const orgOptions = useAlphabetisedOrganisations({ orgs: organisations, active: organisation });

  const activeOrg = useMemo(
    () => ({
      label: organisation?.displayName,
      value: organisation?.id,
    }),
    [organisation]
  );

  const handleUpdateActiveOrganisation = async (value: string) => {
    setIsPopoverOpen(false);
    const res = await onUpdateActiveOrganisation(value);
    if (res && /^\/integrations\/[A-Z0-9]+$/.test(location.pathname)) {
      setTimeout(() => {
        navigate({ to: '/home' });
      }, 300);
    }
  };

  const onNavigateToAccount = (route: string, search?: Record<string, unknown>) => {
    setIsPopoverOpen(false);
    handleNavigationToAccount?.(route, search);
  };

  const handleCopyOrganisationId = () => {
    navigator.clipboard.writeText(organisation?.id || '');
    toast({
      title: 'Organisation ID copied',
      type: 'info',
    });
  };

  return (
    <Popover.Root open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <Popover.Trigger>
        <Button size="2" variant="soft">
          Account
        </Button>
      </Popover.Trigger>
      <Popover.Content
        align="end"
        autoFocus={false}
        sideOffset={10}
        style={{ backgroundColor: 'var(--cyan-2)' }}
        minWidth="25rem"
      >
        <Flex direction="column" gap="2">
          <Avatar handleNavigationToAccount={onNavigateToAccount} />
          <Separator size="4" />
          <Flex direction="column" gap="2" py="1">
            <Flex align="center" gap="2" justify="between" position="relative">
              <Flex align="center" gap="2">
                <TbUsersGroup />
                <Heading size="2" weight="medium">
                  Organisation
                </Heading>
              </Flex>

              <Tooltip content="Copy Organisation ID" side="left">
                <Flex
                  role="button"
                  tabIndex={0}
                  onClick={handleCopyOrganisationId}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      handleCopyOrganisationId();
                    }
                  }}
                  className={Styles.AccountDropdownCopyOrganisationId}
                >
                  <TbCopy />
                </Flex>
              </Tooltip>
            </Flex>
            <Select.Root
              defaultValue={activeOrg.value}
              onValueChange={(value) => handleUpdateActiveOrganisation(value)}
            >
              <Select.Trigger placeholder="Select Organisation" />
              <Select.Content position="popper" align="end" style={{ maxHeight: '20rem', overflow: 'auto' }}>
                {orgOptions?.map((org) => (
                  <Select.Item key={org.value} value={org.value}>
                    {org.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>
          <Separator size="4" />
          <a
            href="https://docs.versori.com"
            target="_blank"
            rel="noopener noreferrer"
            className={Styles.AccountDropdownLink}
          >
            <Flex align="center" gap="2">
              <TbBook color="inherit" />
              <Text size="2" weight="medium" style={{ color: 'inherit' }}>
                Documentation
              </Text>
            </Flex>
          </a>
          <Separator size="4" />

          <Flex
            role="button"
            tabIndex={0}
            py="1"
            onClick={handleLogout}
            className={Styles.AccountDropdownLogout}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                handleLogout();
              }
            }}
          >
            <Flex align="center" gap="2">
              <TbLogout color="var(--red-9)" />
              <Text size="2" weight="medium" style={{ color: 'inherit' }}>
                Logout
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}
